import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/utils';

/**
 * Shared Markdown renderer (GitHub-Flavored Markdown).
 *
 * Raw HTML is NOT enabled (no rehype-raw), so any embedded HTML is rendered inert — safe by
 * default. Links open in a new tab with rel="noopener noreferrer" so following one never
 * navigates away from a live call.
 */

/** Prose styles for rendered markdown (no @tailwindcss/typography dependency needed). */
const markdownStyles = [
  // Spacing between block elements
  '[&>*+*]:mt-3',
  '[&>*:first-child]:mt-0',
  '[&>*:last-child]:mb-0',
  // Paragraphs
  '[&_p]:leading-relaxed',
  // Bold
  '[&_strong]:font-semibold',
  // Links
  '[&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2',
  // Lists
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1',
  '[&_li]:leading-relaxed',
  // Inline code
  '[&_code]:rounded [&_code]:bg-black/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_code]:font-mono',
  // Code blocks
  '[&_pre]:rounded-md [&_pre]:bg-black/10 [&_pre]:p-3 [&_pre]:overflow-x-auto',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  // Tables
  '[&_table]:w-full [&_table]:border-collapse [&_table]:text-xs',
  '[&_th]:border [&_th]:border-border [&_th]:bg-black/5 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-1.5',
  // Headings
  '[&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold [&_h3]:text-sm [&_h3]:font-semibold',
].join(' ');

const markdownComponents: Components = {
  // react-markdown passes a `node` prop that must not be spread onto the DOM element.
  a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
};

interface MarkdownProps {
  /** Raw markdown source. */
  children: string;
  /** Extra classes for the wrapper (lets the caller scope typography/size to its container). */
  className?: string;
}

/** Renders a GFM markdown string with shared prose styles and safe links. */
export const Markdown: React.FC<MarkdownProps> = ({ children, className }) => (
  <div className={cn('max-w-none', markdownStyles, className)}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {children}
    </ReactMarkdown>
  </div>
);
